import React, { type ComponentProps } from 'react'

import type { useSlider } from '@fourthwall/components'
import { BaseContent } from '@fourthwall/components'
import { type ProductBox, ProductColorVariantProvider } from '@fourthwall/module-product'
import { useWebViewBridge } from '@fourthwall/webview-bridge'
import { routing } from '@utils/routing'

import { SliderProductBox } from '../SliderProductBox/SliderProductBox'
import * as S from './styled'
import { getProductHref } from './utils'

type Props = {
  products: ComponentProps<typeof ProductBox>['product'][]
  activeCategory: 'recommendations' | 'favorites'
  slider: ReturnType<typeof useSlider>
  onProductClick: (product: ComponentProps<typeof ProductBox>['product']) => void
}

export const Slider = ({ products, activeCategory, slider, onProductClick }: Props) => {
  const { methods, isWebView } = useWebViewBridge()

  if (!products.length) {
    return (
      <S.EmptyState>
        <BaseContent>
          <p>You don’t have any favorite products added yet</p>
          <p>
            <a
              {...(isWebView
                ? {
                    onClick: async () => {
                      methods.openMobilePage?.({
                        mobile_path: 'product_catalog',
                        url: routing.productCatalog.self,
                      })
                    },
                    href: routing.productCatalog.self,
                  }
                : {
                    href: routing.productCatalog.self,
                  })}
              rel="noreferrer"
              data-analytics="browseAllProducts"
            >
              Browse product catalog
            </a>
          </p>
        </BaseContent>
      </S.EmptyState>
    )
  }

  return (
    <S.SliderWrapper>
      <S.Slider {...slider.getSliderProps()}>
        {products.map((product) => {
          const { url, path } = getProductHref(product.slug)

          return (
            <S.Slide
              key={product.id}
              target="_blank"
              {...(isWebView
                ? {
                    onClick: async () => {
                      onProductClick(product)
                      methods.openMobilePage?.({
                        mobile_path: 'product_catalog',
                        url: path,
                      })
                    },
                  }
                : {
                    onClick: () => onProductClick(product),
                    href: url,
                  })}
              data-analytics="viewProduct"
              {...slider.getSlideProps()}
            >
              <ProductColorVariantProvider colorVariants={product.variants.matched}>
                <SliderProductBox product={product} href={url} activeCategory={activeCategory} />
              </ProductColorVariantProvider>
            </S.Slide>
          )
        })}
      </S.Slider>
    </S.SliderWrapper>
  )
}
